import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { ProcessVisualizationWidget } from '../../process-graph-widget/index.js';
import { max } from 'date-fns';

const ProcessVisualizationWidgetWrapper = ({
  dbSource = 'source_events',
  onNodeClick,
  onFilterChange,
  onDataLoad,
  onDataError,
  className = '',
  showFilters = true,
  showMetrics = true,
  showZoomControls = true,
  maxHeight = '100%',
  messages = {},
}) => {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerId] = useState(`process-widget-${Math.random().toString(36).substr(2, 9)}`);

  // Stable callback references
  const stableOnDataLoad = useCallback((data) => {
    setLoading(false);
    if (onDataLoad) onDataLoad(data);
  }, [onDataLoad]);

  const stableOnDataError = useCallback((err) => {
    setError(err.message);
    setLoading(false);
    if (onDataError) onDataError(err);
  }, [onDataError]);

  // Initialize widget only once
  useEffect(() => {
    if (!containerRef.current || widgetRef.current) return;

    const initializeWidget = async () => {
      try {
        setLoading(true);
        setError(null);

        // Create widget configuration
        const config = {
          dataSource: {
            type: 'api',
            api: {
              baseUrl: (process.env.REACT_APP_NEXTLOG_API_URL),
              endpoint: '/process_graph_description',
              dbSource: dbSource
            }
          },
          ui: {
            showFilters: showFilters,
            showMetrics: showMetrics,
            showZoomControls: showZoomControls,
          },
          callbacks: {
            onNodeClick: onNodeClick,
            onFilterChange: onFilterChange,
            onDataLoad: stableOnDataLoad,
            onDataError: stableOnDataError
          },
          messages: messages,
        };

        // Initialize widget
        widgetRef.current = new ProcessVisualizationWidget(containerId, config);
        await widgetRef.current.init();

      } catch (err) {
        console.error('Widget initialization error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    initializeWidget();

    // Cleanup
    return () => {
      if (widgetRef.current) {
        widgetRef.current.destroy();
        widgetRef.current = null;
      }
    };
  }, [containerId]); // Only depend on containerId

  // Handle data source changes without recreating the widget
  useEffect(() => {
    if (widgetRef.current && widgetRef.current.isInitialized) {
      // Update the data source configuration
      widgetRef.current.configManager.set('dataSource.api.dbSource', dbSource);
      widgetRef.current.config = widgetRef.current.configManager.getAll();
      widgetRef.current.dataSourceManager.updateConfig(widgetRef.current.config);
      
      // Reload data with new source
      widgetRef.current.loadData();
    }
  }, [dbSource]);

  // Update callbacks when they change without recreating the widget
  useEffect(() => {
    if (widgetRef.current && widgetRef.current.isInitialized) {
      // Update callbacks in the configuration
      widgetRef.current.configManager.set('callbacks.onNodeClick', onNodeClick);
      widgetRef.current.configManager.set('callbacks.onFilterChange', onFilterChange);
      widgetRef.current.config = widgetRef.current.configManager.getAll();
    }
  }, [onNodeClick, onFilterChange]);

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Widget Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div className={`position-relative ${className}`}>
      {loading && (
        <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 1000 }}>
          <Spinner animation="border" role="status" variant="primary" />
          <p className="mt-2">Loading process visualization...</p>
        </div>
      )}
      <div 
        id={containerId}
        ref={containerRef}
        // style={{ minHeight: '550px', width: '100%', maxHeight: maxHeight }}
        style={{ height: 'calc(100vh - 150px)', minHeight: '550px', width: '100%', maxHeight: maxHeight }}
      />
    </div>
  );
};

export default ProcessVisualizationWidgetWrapper;